import { useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { 
  EllipsisVerticalIcon,
  HeartIcon,
  HandThumbUpIcon,
  FaceSmileIcon,
  PencilIcon,
  TrashIcon,
  ArrowUturnLeftIcon
} from '@heroicons/react/24/outline'
import { HeartIcon as HeartIconSolid, HandThumbUpIcon as HandThumbUpIconSolid } from '@heroicons/react/24/solid'

const MessageBubble = ({ message, currentUser, isFirstInGroup, isLastInGroup }) => {
  const [showMenu, setShowMenu] = useState(false)
  const [showReactions, setShowReactions] = useState(false)
  
  const isOwnMessage = message.sender._id === currentUser?._id
  const hasReactions = message.reactions && message.reactions.length > 0

  const handleReaction = (emoji) => {
    // TODO: Implement reaction toggle
    console.log('Toggle reaction:', emoji, message._id)
    setShowReactions(false)
  }

  const handleEdit = () => {
    // TODO: Implement message editing
    console.log('Edit message:', message._id)
    setShowMenu(false)
  }

  const handleDelete = () => {
    // TODO: Implement message deletion
    console.log('Delete message:', message._id)
    setShowMenu(false)
  }

  const handleReply = () => {
    // TODO: Implement reply functionality
    console.log('Reply to message:', message._id)
    setShowMenu(false)
  }

  const renderMessageContent = () => {
    if (message.isDeleted) {
      return (
        <div className="italic text-gray-500 dark:text-gray-400">
          This message was deleted
        </div>
      )
    }

    switch (message.messageType) {
      case 'image':
        return (
          <div className="space-y-2">
            {message.attachments?.map((attachment, index) => (
              <img
                key={index}
                src={attachment.url}
                alt={attachment.filename}
                className="max-w-xs rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => window.open(attachment.url, '_blank')}
              />
            ))}
            {message.content && (
              <p className="text-sm">{message.content}</p>
            )}
          </div>
        )
      
      case 'file':
        return (
          <div className="space-y-2">
            {message.attachments?.map((attachment, index) => (
              <div
                key={index}
                className="flex items-center space-x-2 p-2 bg-gray-100 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                onClick={() => window.open(attachment.url, '_blank')}
              >
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{attachment.filename}</p>
                  <p className="text-xs text-gray-500">
                    {(attachment.fileSize / 1024 / 1024).toFixed(1)} MB
                  </p>
                </div>
              </div>
            ))}
            {message.content && (
              <p className="text-sm">{message.content}</p>
            )}
          </div>
        )
      
      default:
        return <p className="whitespace-pre-wrap break-words">{message.content}</p>
    }
  }

  const getUserReaction = (emoji) => {
    return message.reactions?.find(r => r.user._id === currentUser?._id && r.emoji === emoji)
  }

  return (
    <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} group`}>
      <div className={`flex ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'} items-end space-x-2 max-w-xs lg:max-w-md`}>
        {/* Avatar (only show for first message in group and if not own message) */}
        {!isOwnMessage && isLastInGroup && (
          <div className="flex-shrink-0 w-8 h-8">
            {message.sender.avatar ? (
              <img
                src={message.sender.avatar}
                alt={message.sender.username}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                <span className="text-primary-600 dark:text-primary-400 text-sm font-medium">
                  {message.sender.username.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Spacer for grouped messages */}
        {!isOwnMessage && !isLastInGroup && (
          <div className="w-8"></div>
        )}

        {/* Message bubble */}
        <div className="flex-1">
          {/* Sender name (only for first message in group and if not own message) */}
          {!isOwnMessage && isFirstInGroup && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 ml-3">
              {message.sender.username}
            </p>
          )}

          <div className="relative">
            {/* Message content */}
            <div
              className={`
                message-bubble
                ${isOwnMessage ? 'message-bubble-sent' : 'message-bubble-received'}
                ${isFirstInGroup && isLastInGroup ? 'rounded-lg' : ''}
                ${isFirstInGroup && !isLastInGroup ? (isOwnMessage ? 'rounded-l-lg rounded-t-lg rounded-br-sm' : 'rounded-r-lg rounded-t-lg rounded-bl-sm') : ''}
                ${!isFirstInGroup && isLastInGroup ? (isOwnMessage ? 'rounded-l-lg rounded-b-lg rounded-tr-sm' : 'rounded-r-lg rounded-b-lg rounded-tl-sm') : ''}
                ${!isFirstInGroup && !isLastInGroup ? (isOwnMessage ? 'rounded-l-lg rounded-r-sm' : 'rounded-r-lg rounded-l-sm') : ''}
              `}
            >
              {/* Reply indicator */}
              {message.replyTo && (
                <div className="mb-2 p-2 bg-black bg-opacity-10 rounded border-l-2 border-current">
                  <p className="text-xs opacity-75">Replying to {message.replyTo.sender?.username}</p>
                  <p className="text-xs opacity-90 truncate">{message.replyTo.content}</p>
                </div>
              )}

              {/* Message content */}
              <div className="text-sm">
                {renderMessageContent()}
              </div>

              {/* Message time and edit indicator */}
              <div className="flex items-center justify-end mt-1 space-x-1">
                {message.editedAt && (
                  <span className="text-xs opacity-60">edited</span>
                )}
                <span className="text-xs opacity-60">
                  {formatDistanceToNow(new Date(message.createdAt), { addSuffix: true })}
                </span>
                
                {/* Read receipts for own messages */}
                {isOwnMessage && message.readBy && message.readBy.length > 1 && (
                  <div className="flex -space-x-1">
                    {message.readBy
                      .filter(read => read.user._id !== currentUser._id)
                      .slice(0, 3)
                      .map((read, index) => (
                        <div
                          key={read.user._id}
                          className="w-4 h-4 rounded-full bg-primary-100 dark:bg-primary-900 border border-white dark:border-gray-800 flex items-center justify-center"
                          title={`Read by ${read.user.username}`}
                        >
                          <span className="text-xs text-primary-600 dark:text-primary-400">
                            {read.user.username.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>

            {/* Reactions */}
            {hasReactions && (
              <div className="flex flex-wrap gap-1 mt-1">
                {Object.entries(
                  message.reactions.reduce((acc, reaction) => {
                    acc[reaction.emoji] = (acc[reaction.emoji] || 0) + 1
                    return acc
                  }, {})
                ).map(([emoji, count]) => (
                  <button
                    key={emoji}
                    onClick={() => handleReaction(emoji)}
                    className={`
                      inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs
                      ${getUserReaction(emoji) 
                        ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300' 
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }
                      hover:bg-primary-200 dark:hover:bg-primary-800 transition-colors
                    `}
                  >
                    <span>{emoji}</span>
                    <span>{count}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Action buttons (show on hover) */}
            <div className={`
              absolute top-0 opacity-0 group-hover:opacity-100 transition-opacity
              ${isOwnMessage ? 'left-0 -translate-x-full' : 'right-0 translate-x-full'}
              flex items-center space-x-1 px-2
            `}>
              {/* Quick reactions */}
              <div className="relative">
                <button
                  onClick={() => setShowReactions(!showReactions)}
                  className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <FaceSmileIcon className="w-4 h-4 text-gray-500" />
                </button>

                {showReactions && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowReactions(false)}
                    />
                    <div className="absolute bottom-full mb-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2 flex space-x-1 z-20">
                      {['❤️', '👍', '😂', '😮', '😢', '😡'].map(emoji => (
                        <button
                          key={emoji}
                          onClick={() => handleReaction(emoji)}
                          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Reply */}
              <button
                onClick={handleReply}
                className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <ArrowUturnLeftIcon className="w-4 h-4 text-gray-500" />
              </button>

              {/* Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <EllipsisVerticalIcon className="w-4 h-4 text-gray-500" />
                </button>

                {showMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowMenu(false)}
                    />
                    <div className="absolute bottom-full mb-2 right-0 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20 min-w-[120px]">
                      {isOwnMessage && !message.isDeleted && (
                        <>
                          <button
                            onClick={handleEdit}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                          >
                            <PencilIcon className="w-4 h-4" />
                            <span>Edit</span>
                          </button>
                          <button
                            onClick={handleDelete}
                            className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                          >
                            <TrashIcon className="w-4 h-4" />
                            <span>Delete</span>
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MessageBubble